/**
 * The one model behind Your Plan: the screen renders it and the downloadable report renders it, and
 * neither reads plan figures from anywhere else, so they always agree.
 *
 * Built from the plan client (with or without self-funding, see `buildYourPlanClient`) once the
 * coverage assessment is ready; until then its state passes through, so nothing missing is shown as $0.
 * Rows add up exactly: self-funding plus policy coverage is the engine's combined coverage. Policy
 * coverage is listed per policy (an out-of-date quote is listed as not counted), with the annuity and
 * any room-and-board policy benefits as their own lines. Applications keep their terms as submitted.
 */
import { isPolicyCountedInPlan } from '@shared';
import type { ConsumerPolicyEntry } from '@shared/consumerPlan';
import type { FundingAssessment, FundingCalculation } from '@/features/funding-sources';
import type { YourPlanPolicyRow, YourPlanReportModel } from '@/features/pdf-report';
import { CONSUMER_POLICY_STATUS_LABEL } from './copy';
import { policyCarrierName, policyDisplayName } from './policy-name';

// Coverage under a dollar is rounding left over from the engine's sums, not a policy benefit.
const ROUNDING_TOLERANCE = 0.5;

interface YourPlanModelInput {
  planClient: Client | null;
  assessment: FundingCalculation<FundingAssessment>;
  policies: readonly ConsumerPolicyEntry[];
  applications: readonly ConsumerApplicationSubmission[];
  selectedQuoteKey: string | null;
  includesSelfFunding: boolean;
}

function policyRows(
  planClient: Client,
  policies: readonly ConsumerPolicyEntry[],
): YourPlanPolicyRow[] {
  const coverageKey = planClient.intakeSurvey?.clientHasStartedLtc
    ? 'policyCoverageOfRemainingLtcCosts'
    : 'projectedCostCoverage';
  const rows: YourPlanPolicyRow[] = policies.map(({ fundingSourceId, policy, status }) => ({
    key: fundingSourceId,
    name: policyDisplayName(policy),
    carrierName: policyCarrierName(policy),
    status,
    statusLabel: CONSUMER_POLICY_STATUS_LABEL[status],
    projectedCoverage: isPolicyCountedInPlan(policy) ? (policy[coverageKey] ?? 0) : null,
    isAddedQuote: status !== 'existing-policy',
  }));

  const annuity = planClient.fundingSources?.annuity;
  const annuityCoverage =
    annuity && !annuity.isDeactivated ? (annuity.annuityProjectedCostCoverage ?? 0) : 0;
  if (annuityCoverage > 0) {
    rows.push({
      key: 'annuity',
      name: 'Annuity',
      carrierName: null,
      status: 'existing-policy',
      statusLabel: CONSUMER_POLICY_STATUS_LABEL['existing-policy'],
      projectedCoverage: annuityCoverage,
      isAddedQuote: false,
    });
  }
  return rows;
}

export function buildYourPlanReportModel({
  planClient,
  assessment,
  policies,
  applications,
  selectedQuoteKey,
  includesSelfFunding,
}: YourPlanModelInput): FundingCalculation<YourPlanReportModel> {
  if (assessment.status !== 'ready') return assessment;
  if (!planClient) return { status: 'loading' };

  const { totalLtcCost, combinedCoverage, gap } = assessment.value;
  const selfFundingInCombined = includesSelfFunding
    ? (planClient.multipleFundingSources.multipleSelfFundingProjectedCostCoverage ?? 0)
    : 0;
  const rows = policyRows(planClient, policies);
  const policyTotal = combinedCoverage - selfFundingInCombined;

  const listed = rows.reduce((sum, row) => sum + (row.projectedCoverage ?? 0), 0);
  const otherBenefits = policyTotal - listed;
  if (otherBenefits >= ROUNDING_TOLERANCE) {
    rows.push({
      key: 'other-policy-benefits',
      name: 'Room and board and other policy benefits',
      carrierName: null,
      status: null,
      statusLabel: null,
      projectedCoverage: otherBenefits,
      isAddedQuote: false,
    });
  }

  return {
    status: 'ready',
    value: {
      estimatedCost: totalLtcCost,
      selfFunding: selfFundingInCombined >= ROUNDING_TOLERANCE ? selfFundingInCombined : null,
      policies: rows,
      // Shown when a policy actually counts (or the engine counts policy benefits); a plan whose only
      // policy needs an updated quote lists it as not counted without a $0 policy total.
      policyTotal:
        rows.some(row => row.projectedCoverage !== null) || policyTotal >= ROUNDING_TOLERANCE
          ? policyTotal
          : null,
      combined: combinedCoverage,
      gap,
      combinedPercent:
        planClient.fundingSources?.combinedProjectedCostCoveragePercent ??
        (totalLtcCost > 0 ? combinedCoverage / totalLtcCost : 1),
      applications: applications.map(({ id, submittedAt, coverage }) => ({
        id,
        submittedAt: String(submittedAt),
        coverage,
        inFundingPlan: coverage.quoteKey === selectedQuoteKey,
      })),
      includesSelfFunding,
    },
  };
}
